import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, Clock, CheckCircle, Eye, Download, Star, Building, Phone, Mail, MapPin, FileText, X, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { RatingForm, RatingDisplay, Rating } from '../components/RatingSystem';

interface Orcamento {
  id: number;
  pedido_id: number;
  fornecedor_id: number;
  fornecedor_nome: string;
  fornecedor_telefone: string;
  fornecedor_email: string;
  fornecedor_cidade: string;
  fornecedor_estado: string;
  codigo_interno: string;
  valor: number;
  condicao_peca: string;
  garantia: string;
  tempo_entrega: string;
  frete_incluso: boolean;
  observacoes: string;
  arquivo: string;
  status: string;
  created_at: string;
}

interface Pedido {
  id: number;
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  tipo_peca: string;
  descricao: string;
  status: string;
  created_at: string;
}

const OrcamentosCliente: React.FC = () => {
  const navigate = useNavigate();
  const [orcamentos, setOrcamentos] = useState<Orcamento[]>([]);
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrcamento, setSelectedOrcamento] = useState<Orcamento | null>(null);
  const [showFornecedorDetails, setShowFornecedorDetails] = useState(false);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [selectedFornecedorForRating, setSelectedFornecedorForRating] = useState<{
    id: number;
    nome: string;
    pedido_id: number;
  } | null>(null);
  
  // Estados para reprovar orçamento
  const [showReprovarModal, setShowReprovarModal] = useState(false);
  const [orcamentoParaReprovar, setOrcamentoParaReprovar] = useState<Orcamento | null>(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');
  const [isReprovandoLoading, setIsReprovandoLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/cliente/login');
      return;
    }

    carregarOrcamentos();
  }, [navigate]);

  const carregarOrcamentos = async () => {
    setIsLoading(true);
    try {
      // Simular dados de orçamentos
      const orcamentosMock = [
        {
          id: 1,
          pedido_id: 1,
          fornecedor_id: 1,
          fornecedor_nome: 'Peças Express Ltda',
          fornecedor_telefone: '(11) 99999-9999',
          fornecedor_email: 'contato@pecasexpress.com.br',
          fornecedor_cidade: 'São Paulo',
          fornecedor_estado: 'SP',
          codigo_interno: 'CAT-320D-001',
          valor: 2500.00,
          condicao_peca: 'nova',
          garantia: '12 meses',
          tempo_entrega: '3 dias úteis',
          frete_incluso: true,
          observacoes: 'Peça original Caterpillar, garantia de fábrica. Entrega em todo Brasil.',
          arquivo: 'orcamento_pecasexpress.pdf',
          status: 'aguardando_orcamentos',
          created_at: '2024-01-15T10:30:00Z'
        },
        {
          id: 2,
          pedido_id: 1,
          fornecedor_id: 2,
          fornecedor_nome: 'Mecânica Industrial Santos',
          fornecedor_telefone: '(13) 88888-8888',
          fornecedor_email: 'vendas@mecanicasantos.com.br',
          fornecedor_cidade: 'Santos',
          fornecedor_estado: 'SP',
          codigo_interno: 'MIS-001',
          valor: 2200.00,
          condicao_peca: 'recondicionada',
          garantia: '6 meses',
          tempo_entrega: '5 dias úteis',
          frete_incluso: false,
          observacoes: 'Peça recondicionada com qualidade garantida. Frete por conta do cliente.',
          arquivo: 'orcamento_mecanica.pdf',
          status: 'aguardando_orcamentos',
          created_at: '2024-01-15T11:15:00Z'
        },
        {
          id: 3,
          pedido_id: 1,
          fornecedor_id: 3,
          fornecedor_nome: 'Distribuidora de Peças Ltda',
          fornecedor_telefone: '(21) 77777-7777',
          fornecedor_email: 'orcamentos@distribuidora.com.br',
          fornecedor_cidade: 'Rio de Janeiro',
          fornecedor_estado: 'RJ',
          codigo_interno: 'DP-320D-001',
          valor: 2800.00,
          condicao_peca: 'nova',
          garantia: '18 meses',
          tempo_entrega: '2 dias úteis',
          frete_incluso: true,
          observacoes: 'Peça nova com garantia estendida. Entrega rápida em todo Brasil.',
          arquivo: 'orcamento_distribuidora.pdf',
          status: 'aguardando_orcamentos',
          created_at: '2024-01-15T12:00:00Z'
        }
      ];

      const pedidoMock = {
        id: 1,
        tipo_equipamento: 'Escavadeira',
        marca: 'Caterpillar',
        modelo: '320D',
        tipo_peca: 'Motor',
        descricao: 'Bomba de óleo para motor, necessito urgente para manutenção preventiva',
        status: 'com_orcamentos',
        created_at: '2024-01-15T09:00:00Z'
      };

      setOrcamentos(orcamentosMock);
      setPedido(pedidoMock);
      setIsLoading(false);

      // Mock de avaliações
      setRatings([
        {
          id: '1',
          cliente_id: 1,
          cliente_nome: 'João Silva',
          fornecedor_id: 1,
          fornecedor_nome: 'Peças Express Ltda',
          pedido_id: 1,
          pontuacao: 4.5,
          comentario: 'Excelente atendimento e produto de qualidade. Entrega no prazo combinado.',
          categorias: {
            qualidade: 5,
            prazo: 4,
            comunicacao: 5,
            preco: 4
          },
          created_at: '2024-01-12T15:30:00Z'
        }
      ]);
    } catch (error) {
      toast.error('Erro ao carregar orçamentos');
      setIsLoading(false);
    }
  };

  const handleEscolherOrcamento = async (orcamento: Orcamento) => {
    try {
      // Simular escolha do orçamento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Orçamento escolhido com sucesso!');
      navigate(`/cliente/fornecedor/${orcamento.fornecedor_id}`);
    } catch (error) {
      toast.error('Erro ao escolher orçamento');
    }
  };

  const handleVerDetalhes = (orcamento: Orcamento) => {
    console.log('Ver detalhes clicado:', orcamento);
    setSelectedOrcamento(orcamento);
    setShowFornecedorDetails(true);
    toast.success('Abrindo detalhes do fornecedor...');
  };

  const handleDownloadArquivo = (arquivo: string) => {
    if (!arquivo) {
      toast.error('Arquivo não disponível');
      return;
    }

    // Simular download do arquivo
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,JVBERi0xLjQKJcOkw7zDtsO...`; // Base64 simulado
    link.download = arquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Arquivo ${arquivo} baixado com sucesso!`);
  };

  const handleSubmitRating = async (ratingData: Omit<Rating, 'id' | 'cliente_id' | 'cliente_nome' | 'created_at'>) => {
    try {
      const newRating: Rating = {
        id: Date.now().toString(),
        cliente_id: 1,
        cliente_nome: 'João Silva',
        ...ratingData,
        created_at: new Date().toISOString()
      };
      
      setRatings(prev => [newRating, ...prev]);
      setShowRatingForm(false);
      setSelectedFornecedorForRating(null);
      toast.success('Avaliação enviada com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar avaliação');
    }
  };

  // Função para reprovar orçamento
  const handleReprovarOrcamento = async () => {
    if (!orcamentoParaReprovar) return;
    
    setIsReprovandoLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/orcamentos/${orcamentoParaReprovar.id}/reprovar`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ motivo: motivoReprovacao })
      });

      if (response.ok) {
        toast.success('Orçamento reprovado com sucesso!');
        setShowReprovarModal(false);
        setOrcamentoParaReprovar(null);
        setMotivoReprovacao('');
        // Recarregar orçamentos
        window.location.reload();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao reprovar orçamento');
      }
    } catch (error) {
      console.error('Erro ao reprovar orçamento:', error);
      toast.error('Erro ao reprovar orçamento');
    } finally {
      setIsReprovandoLoading(false);
    }
  };

  const handleRateFornecedor = (fornecedor_id: number, fornecedor_nome: string, pedido_id: number) => {
    setSelectedFornecedorForRating({ id: fornecedor_id, nome: fornecedor_nome, pedido_id });
    setShowRatingForm(true);
  };

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCondicaoText = (condicao: string) => {
    switch (condicao) {
      case 'nova':
        return 'Nova';
      case 'usada':
        return 'Usada';
      case 'recondicionada':
        return 'Recondicionada';
      default:
        return condicao;
    }
  };

  const getCondicaoColor = (condicao: string) => {
    switch (condicao) {
      case 'nova':
        return 'bg-green-100 text-green-800';
      case 'usada':
        return 'bg-yellow-100 text-yellow-800';
      case 'recondicionada':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b-2 border-red sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red rounded-lg">
                  <Package className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-red uppercase">
                  PeçaJá
                </h1>
              </div>
              <button onClick={() => navigate('/cliente/dashboard')} className="btn btn-outline">
                Voltar
              </button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <h3 className="text-xl font-semibold text-gray-dark mb-2">Carregando...</h3>
            <p className="text-gray-medium">Buscando orçamentos disponíveis</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b-2 border-red sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <button onClick={() => navigate('/cliente/dashboard')} className="btn btn-outline">
              Voltar
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Pedido Info */}
        {pedido && (
          <div className="card mb-8">
            <h2 className="text-2xl font-bold text-gray-dark mb-4 flex items-center">
              <Package className="h-6 w-6 mr-2" />
              Detalhes do Pedido
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-medium mb-1">Equipamento</p>
                <p className="text-gray-dark font-semibold">{pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}</p>
              </div>
              <div>
                <p className="text-gray-medium mb-1">Tipo de Peça</p>
                <p className="text-gray-dark font-semibold">{pedido.tipo_peca}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-gray-medium mb-1">Descrição</p>
                <p className="text-gray-dark">{pedido.descricao}</p>
              </div>
            </div>
          </div>
        )}

        {/* Orçamentos */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-dark flex items-center">
              <CheckCircle className="h-6 w-6 mr-2 text-green-600" />
              Orçamentos Recebidos
            </h2>
            <div className="text-sm text-gray-medium">
              {orcamentos.length} orçamento(s) disponível(is)
            </div>
          </div>

          {orcamentos.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-medium mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-dark mb-2">Nenhum orçamento recebido</h3>
              <p className="text-gray-medium">Aguarde os fornecedores enviarem suas propostas.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {orcamentos.map((orcamento, index) => (
                <div key={orcamento.id} className="border-2 border-gray-light rounded-lg p-6 hover:border-red transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          <Star className="h-5 w-5 text-yellow-500" />
                          <span className="text-lg font-bold text-gray-dark">
                            {index + 1}º Orçamento
                          </span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCondicaoColor(orcamento.condicao_peca)}`}>
                          {getCondicaoText(orcamento.condicao_peca)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-gray-medium mb-1">Fornecedor</p>
                          <p className="text-gray-dark font-semibold">{orcamento.fornecedor_nome}</p>
                          <p className="text-sm text-gray-medium">{orcamento.fornecedor_cidade} - {orcamento.fornecedor_estado}</p>
                        </div>
                        <div>
                          <p className="text-gray-medium mb-1">Valor</p>
                          <p className="text-2xl font-bold text-red">{formatarValor(orcamento.valor)}</p>
                        </div>
                        <div>
                          <p className="text-gray-medium mb-1">Tempo de Entrega</p>
                          <p className="text-gray-dark">{orcamento.tempo_entrega}</p>
                        </div>
                        <div>
                          <p className="text-gray-medium mb-1">Garantia</p>
                          <p className="text-gray-dark">{orcamento.garantia}</p>
                        </div>
                        <div>
                          <p className="text-gray-medium mb-1">Código Interno</p>
                          <p className="text-gray-dark">{orcamento.codigo_interno || 'Não informado'}</p>
                        </div>
                        <div>
                          <p className="text-gray-medium mb-1">Status</p>
                          <p className="text-gray-dark capitalize">{orcamento.status}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-medium mb-3">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Recebido em {formatarData(orcamento.created_at)}
                        </span>
                        {orcamento.frete_incluso && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            Frete incluso
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          {orcamento.fornecedor_telefone}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {orcamento.fornecedor_email}
                        </span>
                      </div>

                      {orcamento.observacoes && (
                        <div className="mb-3">
                          <p className="text-gray-medium mb-1">Observações</p>
                          <p className="text-gray-dark text-sm">{orcamento.observacoes}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 mt-4 lg:mt-0">
                      <button
                        onClick={() => handleEscolherOrcamento(orcamento)}
                        className="btn btn-primary w-full"
                        type="button"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Escolher Este
                      </button>
                      <button
                        onClick={() => {
                          setOrcamentoParaReprovar(orcamento);
                          setShowReprovarModal(true);
                        }}
                        className="btn bg-red-600 hover:bg-red-700 text-white w-full"
                        type="button"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reprovar
                      </button>
                      <button
                        onClick={() => handleVerDetalhes(orcamento)}
                        className="btn btn-outline w-full cursor-pointer hover:bg-gray-light"
                        type="button"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Ver Detalhes
                      </button>
                      {orcamento.arquivo && (
                        <button
                          onClick={() => handleDownloadArquivo(orcamento.arquivo)}
                          className="btn btn-outline w-full cursor-pointer hover:bg-gray-light"
                          type="button"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </button>
                      )}
                      <button
                        onClick={() => handleRateFornecedor(orcamento.fornecedor_id, orcamento.fornecedor_nome, orcamento.pedido_id)}
                        className="btn btn-outline w-full cursor-pointer hover:bg-gray-light"
                        type="button"
                      >
                        <Star className="h-4 w-4 mr-2" />
                        Avaliar Fornecedor
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal de Detalhes do Fornecedor */}
      {showFornecedorDetails && selectedOrcamento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center modal-overlay p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto modal-content">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-dark">Detalhes do Fornecedor</h3>
                <button
                  onClick={() => setShowFornecedorDetails(false)}
                  className="text-gray-medium hover:text-gray-dark"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Informações da Empresa */}
                <div className="p-4 bg-gray-light rounded-lg">
                  <h4 className="font-semibold text-gray-dark mb-3 flex items-center">
                    <Building className="h-5 w-5 mr-2" />
                    Informações da Empresa
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-medium mb-1">Nome</p>
                      <p className="text-gray-dark font-semibold">{selectedOrcamento.fornecedor_nome}</p>
                    </div>
                    <div>
                      <p className="text-gray-medium mb-1">Localização</p>
                      <p className="text-gray-dark">{selectedOrcamento.fornecedor_cidade} - {selectedOrcamento.fornecedor_estado}</p>
                    </div>
                  </div>
                </div>

                {/* Contatos */}
                <div className="p-4 bg-gray-light rounded-lg">
                  <h4 className="font-semibold text-gray-dark mb-3">Contatos</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-medium" />
                      <span className="text-gray-dark">{selectedOrcamento.fornecedor_telefone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-medium" />
                      <span className="text-gray-dark">{selectedOrcamento.fornecedor_email}</span>
                    </div>
                  </div>
                </div>

                {/* Detalhes do Orçamento */}
                <div className="p-4 bg-gray-light rounded-lg">
                  <h4 className="font-semibold text-gray-dark mb-3">Detalhes do Orçamento</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-medium mb-1">Código Interno</p>
                      <p className="text-gray-dark">{selectedOrcamento.codigo_interno || 'Não informado'}</p>
                    </div>
                    <div>
                      <p className="text-gray-medium mb-1">Valor</p>
                      <p className="text-xl font-bold text-red">{formatarValor(selectedOrcamento.valor)}</p>
                    </div>
                    <div>
                      <p className="text-gray-medium mb-1">Condição</p>
                      <p className="text-gray-dark">{getCondicaoText(selectedOrcamento.condicao_peca)}</p>
                    </div>
                    <div>
                      <p className="text-gray-medium mb-1">Garantia</p>
                      <p className="text-gray-dark">{selectedOrcamento.garantia}</p>
                    </div>
                    <div>
                      <p className="text-gray-medium mb-1">Tempo de Entrega</p>
                      <p className="text-gray-dark">{selectedOrcamento.tempo_entrega}</p>
                    </div>
                    <div>
                      <p className="text-gray-medium mb-1">Frete</p>
                      <p className="text-gray-dark">
                        {selectedOrcamento.frete_incluso ? 'Incluso' : 'Por conta do cliente'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Observações */}
                {selectedOrcamento.observacoes && (
                  <div className="p-4 bg-gray-light rounded-lg">
                    <h4 className="font-semibold text-gray-dark mb-3">Observações</h4>
                    <p className="text-gray-dark">{selectedOrcamento.observacoes}</p>
                  </div>
                )}

                {/* Arquivo */}
                {selectedOrcamento.arquivo && (
                  <div className="p-4 bg-gray-light rounded-lg">
                    <h4 className="font-semibold text-gray-dark mb-3 flex items-center">
                      <FileText className="h-5 w-5 mr-2" />
                      Arquivo do Orçamento
                    </h4>
                    <button
                      onClick={() => handleDownloadArquivo(selectedOrcamento.arquivo)}
                      className="btn btn-outline cursor-pointer hover:bg-gray-light"
                      type="button"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download do Arquivo
                    </button>
                  </div>
                )}

                {/* Botões de Ação */}
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => handleEscolherOrcamento(selectedOrcamento)}
                    className="btn btn-primary flex-1"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Escolher Este Orçamento
                  </button>
                  <button
                    onClick={() => setShowFornecedorDetails(false)}
                    className="btn btn-outline flex-1"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Avaliação */}
      {showRatingForm && selectedFornecedorForRating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <RatingForm
              fornecedor_id={selectedFornecedorForRating.id}
              fornecedor_nome={selectedFornecedorForRating.nome}
              pedido_id={selectedFornecedorForRating.pedido_id}
              onSubmit={handleSubmitRating}
              onCancel={() => {
                setShowRatingForm(false);
                setSelectedFornecedorForRating(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Modal de Reprovação */}
      {showReprovarModal && orcamentoParaReprovar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Reprovar Orçamento</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja reprovar o orçamento de <strong>{orcamentoParaReprovar.fornecedor_nome}</strong>?
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo da reprovação (opcional)
              </label>
              <textarea
                value={motivoReprovacao}
                onChange={(e) => setMotivoReprovacao(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={3}
                placeholder="Digite o motivo da reprovação..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReprovarModal(false);
                  setOrcamentoParaReprovar(null);
                  setMotivoReprovacao('');
                }}
                className="btn btn-outline"
                disabled={isReprovandoLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleReprovarOrcamento}
                className="btn bg-red-600 hover:bg-red-700 text-white"
                disabled={isReprovandoLoading}
              >
                {isReprovandoLoading ? 'Reprovando...' : 'Confirmar Reprovação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrcamentosCliente; 